import { gql } from "@apollo/client";
import { datef } from "@caredna-marlin/utils/dates";
import CommentIcon from '@mui/icons-material/Comment';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TableRow,
  TableCell,
  Tooltip,
  Select,
  MenuItem,
  TextField,
  Button,
  FormControl,
  Divider,
  InputLabel,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { capitalize, groupBy } from "lodash";

import { useId, useState } from "react";
import LazyLoadTable from "~/components/common/LazyLoadTable";
import { SupplyChangeReasonEventTypeEnum as ET, OrderDirection, SupplyChangeReasonEventTypeEnum, SupplyEventMutationVariables, SupplyEventOrderBy, SupplyItemDetailsQuery, SupplyItemDetailsQueryVariables, useSupplyEventMutation, useSupplyItemDetailsQuery } from "~/graphql/__generated__/graphql";
import { useOrgConfig } from "~/providers/OrgConfigProvider";

export const ACTION_LABEL: Record<SupplyChangeReasonEventTypeEnum, string> = {
  withdrawal: "withdrew",
  deposit: "deposited",
  recount: "counted",
}

const cols = [
  { label: "User", sort: [] },
  { label: "Action", sort: [] },
  { label: "Reason", sort: [] },
  { label: "At", sort: ["createdAt"] },
]

type Props = {
  onClose: () => void;
  supplyItem: { id: number, currentStock: number, label: string, barcode: string, needsRecount: boolean };
}

const SupplyItemPopup = ({ onClose, supplyItem }: Props) => {
  const { supplyChangeReasons } = useOrgConfig();
  const [eventType, setEventType] = useState<ET>(ET.Withdrawal);

  const reasons = groupBy(supplyChangeReasons, "eventType")

  const [eventVariables, setEventVariables] = useState<SupplyEventMutationVariables>(() => ({
    supplyItemId: supplyItem.id,
    supplyChangeReasonId: reasons[eventType]?.[0]?.id ?? 0,
    note: "",
    amount: 1,
    resetNeedsReconciliation: false,
  }));

  const validReasonSelected = !!reasons[eventType]?.find(r => r?.id === eventVariables.supplyChangeReasonId);

  const [queryVariables, setQueryVariables] = useState<SupplyItemDetailsQueryVariables>({
    supplyItemId: supplyItem.id,
    offset: 0,
    orderBy: { createdAt: { direction: OrderDirection.Desc, priority: 1 }} as SupplyEventOrderBy,
  });

  const { data, loading } = useSupplyItemDetailsQuery({ variables: queryVariables });
  const [supplyEvent, { loading: eventLoading }] = useSupplyEventMutation();

  const amountLabel = <>
    Amount
    {eventType !== "recount" && supplyItem.needsRecount && <Tooltip title="The count is likely innacurate; you can still withdraw or deposit, but a recount is needed">
      <WarningAmberRoundedIcon color="warning" sx={{ verticalAlign: "bottom", ml: 0.5 }} />
    </Tooltip>}
    {eventType === "withdrawal" && !supplyItem.needsRecount && supplyItem.currentStock < eventVariables.amount && <Tooltip title={`Only ${supplyItem.currentStock} are recorded as in inventory. You can record withdrawing any number, but we'll mark the item as needing a recount.`}>
      <WarningAmberRoundedIcon color="warning" sx={{ verticalAlign: "bottom", ml: 0.5 }} />
    </Tooltip>}
  </>

  const id = useId();

  return (<Dialog open={true} onClose={onClose} slotProps={{ paper: { sx: { maxWidth: 700 }}}}>
    <DialogTitle>
      {supplyItem.label} - {supplyItem.currentStock} in stock
    </DialogTitle>
    <DialogContent>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={4}>
          <FormControl size="small" fullWidth>
            <InputLabel>Action</InputLabel>
            <Select
              label="Action"
              value={eventType}
              onChange={e => {
                const et = e.target.value as ET
                setEventType(et)
                setEventVariables({
                  ...eventVariables,
                  supplyChangeReasonId: reasons[et]?.[0]?.id ?? 0
                })
              }}
              disabled={eventLoading}
            >
              <MenuItem value={ET.Withdrawal}>Withdraw</MenuItem>
              <MenuItem value={ET.Deposit}>Deposit</MenuItem>
              <MenuItem value={ET.Recount}>Recount</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={2}>
          <TextField
            label={amountLabel}
            type="number"
            size="small"
            fullWidth
            value={eventVariables.amount}
            onChange={(e) => setEventVariables({
              ...eventVariables,
              amount: parseInt(e.target.value) > 0 ? parseInt(e.target.value) : 0,
            })}
            disabled={eventLoading}
          />
        </Grid>
        <Grid size={6}>
          <FormControl size="small" fullWidth>
            <InputLabel>Reason</InputLabel>
            <Select
              label="Reason"
              fullWidth
              value={validReasonSelected ? eventVariables.supplyChangeReasonId : ""}
              onChange={e => setEventVariables({
                ...eventVariables,
                supplyChangeReasonId: parseInt(e.target.value.toString()),
              })}
              disabled={eventLoading}
            >
              {reasons[eventType]?.map(r => r && <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={12}>
          <TextField
            label="Note"
            size="small"
            fullWidth
            value={eventVariables.note}
            onChange={(e) => setEventVariables({ ...eventVariables, note: e.target.value })}
            disabled={eventLoading}
          />
        </Grid>
        <Grid size={12}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading || !validReasonSelected || (!eventVariables.amount && eventType !== "recount")}
            loading={eventLoading}
            onClick={async () => {
              await supplyEvent({
                variables: eventVariables,
                awaitRefetchQueries: true,
                refetchQueries: ["SupplyItemDetails", "SupplyItemsPage"]
              })
              onClose()
            }}
          >Save</Button>
        </Grid>
        <Grid size={12}>
          <Divider />
        </Grid>
        <Grid size={12}>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${id}-panel1-content`}
              id={`${id}-panel1-header`}
            >
              <Typography sx={{ fontWeight: 700 }}>Event History</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <LazyLoadTable
                columns={cols}
                emptyLabel="No history"
                items={data?.supplyItemSingle?.supplyEvents}
                loading={loading}
                renderRow={supplyEvent => <SupplyEventRow key={supplyEvent.id} supplyEvent={supplyEvent} />}
                queryVariables={queryVariables}
                setQueryVariables={setQueryVariables}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </DialogContent>
  </Dialog>);
}

export default SupplyItemPopup;

type SupplyEvent = NonNullable<SupplyItemDetailsQuery["supplyItemSingle"]>["supplyEvents"][number];

const SupplyEventRow = ({ supplyEvent }: { supplyEvent: SupplyEvent }) => {
  const { supplyChangeReasons } = useOrgConfig();
  const reason = supplyChangeReasons[supplyEvent.supplyChangeReasonId];

  return <TableRow>
    <TableCell>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center" }}>
        {supplyEvent.createdByUser?.firstName} {supplyEvent.createdByUser?.lastName}
        {supplyEvent.note && <Tooltip title={supplyEvent.note}>
          <CommentIcon fontSize="small" color="primary" />
        </Tooltip>}
      </Stack>
    </TableCell>
    <TableCell>
      {capitalize(ACTION_LABEL[reason?.eventType ?? "recount"])}{" "}
      {supplyEvent.amount}
    </TableCell>
    <TableCell>
      {reason?.label}
    </TableCell>
    <TableCell>
      {datef(new Date(supplyEvent.createdAt), 'MMM d, h:mm aa')}
    </TableCell>
  </TableRow>
}

gql(`
  query SupplyItemDetails($supplyItemId: Int!, $offset: Int!, $orderBy: SupplyEventOrderBy!)
  @permission(requires: useSupplies)
  {
    supplyItemSingle(where: { id: { eq: $supplyItemId } }) {
      id
      label
      barcode
      currentStock
      needsRecount
      active
      supplyEvents(limit: 50, offset: $offset, orderBy: $orderBy) {
        id
        supplyChangeReasonId
        note
        amount
        scannedBarcode
        createdAt
        createdByUser {
          firstName
          lastName
        }
      }
    }
  }
`);

gql(`
  mutation SupplyEvent(
    $supplyItemId: Int!
    $supplyChangeReasonId: Int!
    $note: String!
    $amount: Int!
  )
  @permission(requires: useSupplies)
  {
    supplyEvent(
      supplyItemId: $supplyItemId
      supplyChangeReasonId: $supplyChangeReasonId
      note: $note
      amount: $amount
    ) {
      id
    }
  }
`);
